import type { OverlayStyles, TransformArgs } from "./typesV2";

export class TextOverlay {
    readonly container = document.createElement("div");
    private box = document.createElement("div");
    private field = document.createElement("div");
    private transformField = document.createElement("div");
    private selection = document.createElement("div");
    private editor: HTMLDivElement | null = null;

    constructor(overlayRoot: HTMLElement) {
        this.buildDOM();
        overlayRoot.appendChild(this.container);
    }

    private buildDOM() {
        this.container.style.position = "fixed";
        this.container.style.transformOrigin = "center center";
        this.container.style.pointerEvents = "auto";
        this.container.style.fontSize = "0px";

        this.box.className = "editor-box";
        this.box.style.overflow = "hidden";

        this.field.style.display = "inline-block";
        this.field.style.whiteSpace = "pre-wrap";
        this.field.style.overflowWrap = "break-word";
        this.field.style.wordBreak = "keep-all";
        this.field.dir = "auto";
        this.field.style.border = "none";
        this.field.style.fontSize = "inherit";
        this.field.style.height = "100%";
        this.field.style.background = "transparent";

        this.transformField.style.position = "absolute";
        this.transformField.style.left = "-99999px";
        this.transformField.style.top = "0";
        this.transformField.style.width = "1px";
        this.transformField.style.height = "1px";
        this.transformField.style.opacity = "0";
        this.transformField.style.pointerEvents = "none";
        this.transformField.style.overflow = "hidden";

        this.selection.style.position = "absolute";
        this.selection.style.left = "0";
        this.selection.style.top = "0";
        this.selection.style.right = "0";
        this.selection.style.bottom = "0";
        this.selection.style.pointerEvents = "none";

        this.box.appendChild(this.field);
        this.box.appendChild(this.selection);
        this.container.appendChild(this.box);
        this.container.appendChild(this.transformField);
    }

    bindEditor(editorEl: HTMLDivElement) {
        this.editor = editorEl;

        this.editor.style.outline = "none";
        this.editor.style.background = "transparent";
        this.editor.style.height = "100%";
        this.editor.contentEditable = "true";
        this.editor.tabIndex = 0;
        this.editor.style.overflowY = "clip";

        this.hide();
    }

    getFieldEl(): HTMLDivElement {
        return this.field;
    }

    getTransformFieldEl(): HTMLDivElement {
        return this.transformField;
    }

    updateOverlay(style: OverlayStyles) {
        if (!this.editor) {
            return;
        }

        this.box.style.textAlign = style.textAlign;

        this.field.style.fontSize = `${style.fontSize}px`;
        this.field.style.lineHeight = `${style.lineHeight}px`;
        this.field.style.color = style.color;
        this.field.style.textAlign = style.textAlign;

        this.editor.style.padding = `0 ${style.horizontalPadding}px`;
        this.editor.style.lineHeight = `${style.lineHeight}px`;
        this.editor.style.setProperty("--circle-diameter", `${style.lineHeight / 5}px`);
        this.editor.style.setProperty("--horizontal-padding", `${style.horizontalPadding}px`);
        this.editor.style.setProperty("--text-font-family", style.fontFamily);
    }

    updateTransform({
        x,
        y,
        width,
        height,
        scale,
        rotation,
        fontSize,
        lineHeight,
        fontFamily,
        horizontalPadding,
        textAlign,
    }: TransformArgs) {
        if (!this.editor) {
            return;
        }

        this.container.style.left = `${x}px`;
        this.container.style.top = `${y}px`;
        this.container.style.width = `${width}px`;
        this.container.style.height = `${height}px`;

        this.box.style.width = `${width}px`;
        this.box.style.height = `${height}px`;
        this.box.style.textAlign = textAlign;

        this.field.style.width = `${width}px`;
        this.field.style.fontSize = `${fontSize}px`;
        this.field.style.lineHeight = `${lineHeight}px`;
        this.field.style.textAlign = textAlign;

        this.editor.style.padding = `0 ${horizontalPadding}px`;
        this.editor.style.lineHeight = `${lineHeight}px`;
        this.editor.style.setProperty("--circle-diameter", `${lineHeight / 5}px`);
        this.editor.style.setProperty("--horizontal-padding", `${horizontalPadding}px`);
        this.editor.style.setProperty("--text-font-family", fontFamily);

        this.container.style.transform = `rotate(${rotation}deg) scale(${scale})`;
    }

    show() {
        if (!this.editor) {
            return;
        }

        this.container.style.zIndex = "1";
        this.container.style.pointerEvents = "auto";

        this.editor.style.opacity = "1";
        this.editor.style.pointerEvents = "auto";
        this.editor.style.visibility = "visible";
    }

    hide() {
        if (!this.editor) {
            return;
        }

        this.container.style.zIndex = "-1";
        this.container.style.pointerEvents = "none";

        this.editor.style.opacity = "0";
        this.editor.style.pointerEvents = "none";
        this.editor.style.visibility = "hidden";
    }

    contains(node: Node) {
        return this.container.contains(node);
    }

    showSelectionRects(rects: Array<{ left: number; top: number; width: number; height: number }>) {
        this.clearSelectionRects();

        for (const rect of rects) {
            const el = document.createElement("div");
            el.style.position = "absolute";
            el.style.left = `${rect.left}px`;
            el.style.top = `${rect.top}px`;
            el.style.width = `${rect.width}px`;
            el.style.height = this.field.style.lineHeight;
            el.style.background = "rgba(0, 120, 255, 0.25)";
            this.selection.appendChild(el);
        }
    }

    clearSelectionRects() {
        while (this.selection.firstChild) {
            this.selection.removeChild(this.selection.firstChild);
        }
    }

    getStyleSourceEl(): HTMLElement {
        return this.field;
    }

    getFontFamily() {
        if (!this.editor) {
            return;
        }

        return this.editor.style.getPropertyValue("--text-font-family");
    }
}
